<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import pilotAssistantAPI from 'dashboard/api/pilot/assistant';
import Button from 'dashboard/components-next/button/Button.vue';
import chatwootBotImage from 'dashboard/assets/images/chatwoot_bot.png';
import typingGif from 'dashboard/assets/images/typing.gif';

// Playground 专用 ActionCable 连接器
// 不使用 BaseActionCableConnector，因为它会传递 user_id，导致订阅失败
import { createConsumer } from '@rails/actioncable';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});
const PRESENCE_INTERVAL = 20000;
const RECONNECT_INTERVAL = 1000;

// 临时消息 ID 计数器（避免 Date.now() 重复）
let tempMessageIdCounter = 0;
const generateTempMessageId = () => {
  tempMessageIdCounter += 1;
  return `temp_${Date.now()}_${tempMessageIdCounter}`;
};

class PlaygroundActionCableConnector {
  constructor(
    pubsubToken,
    accountId,
    onMessageCreated,
    onDisconnect,
    onReconnect,
    onStreamingStart,
    onStreaming
  ) {
    this.onMessageCreatedCallback = onMessageCreated;
    this.onDisconnectCallback = onDisconnect;
    this.onReconnectCallback = onReconnect;
    this.onStreamingStartCallback = onStreamingStart;
    this.onStreamingCallback = onStreaming;
    this.isDisconnected = false;
    this.reconnectTimer = null;
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'message.streaming.start': this.onStreamingStart,
      'message.streaming': this.onStreamingEvent,
    };

    // 使用和 Dashboard 相同的 WebSocket URL
    const wsURL = window.chatwootConfig?.websocketURL
      ? `${window.chatwootConfig.websocketURL}/cable`
      : undefined;
    this.consumer = createConsumer(wsURL);
    this.subscription = this.consumer.subscriptions.create(
      {
        channel: 'RoomChannel',
        pubsub_token: pubsubToken,
        account_id: accountId,
        // 不传递 user_id，让后端通过 pubsub_token 查找 ContactInbox
      },
      {
        received: this.onReceived,
        connected: () => {
          console.log('[Playground] Subscription ACCEPTED');
        },
        rejected: () => {
          console.log('[Playground] Subscription REJECTED by server');
        },
        disconnected: () => {
          console.log('[Playground] Subscription DISCONNECTED');
          this.onDisconnected();
        },
      }
    );

    // 定期发送 presence
    this.presenceInterval = setInterval(() => {
      try {
        this.subscription.perform('update_presence');
      } catch (e) {
        console.error('[Playground] Failed to update presence:', e);
      }
    }, PRESENCE_INTERVAL);
  }

  onDisconnected = () => {
    this.isDisconnected = true;
    if (this.onDisconnectCallback) {
      this.onDisconnectCallback();
    }
    this.initReconnectTimer();
  };

  checkConnection = () => {
    const isConnectionActive = this.consumer.connection.isOpen();
    const isReconnected = this.isDisconnected && isConnectionActive;

    if (isReconnected) {
      this.clearReconnectTimer();
      this.isDisconnected = false;
      if (this.onReconnectCallback) {
        this.onReconnectCallback();
      }
    } else {
      this.initReconnectTimer();
    }
  };

  clearReconnectTimer = () => {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  initReconnectTimer = () => {
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.checkConnection();
    }, RECONNECT_INTERVAL);
  };

  onMessageCreated = data => {
    if (this.onMessageCreatedCallback) {
      this.onMessageCreatedCallback(data);
    }
  };

  onMessageUpdated = data => {
    if (this.onMessageCreatedCallback) {
      this.onMessageCreatedCallback(data);
    }
  };

  onStreamingStart = data => {
    if (this.onStreamingStartCallback) {
      this.onStreamingStartCallback(data);
    }
  };

  onStreamingEvent = data => {
    if (this.onStreamingCallback) {
      this.onStreamingCallback(data);
    }
  };

  onReceived = ({ event, data } = {}) => {
    console.log('[Playground] WS received:', event, data?.conversation_id, data?.id);
    if (this.events[event] && typeof this.events[event] === 'function') {
      this.events[event](data);
    }
  };

  disconnect() {
    this.clearReconnectTimer();
    if (this.presenceInterval) {
      clearInterval(this.presenceInterval);
    }
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.consumer) {
      this.consumer.disconnect();
    }
  }
}

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

// 从 Vuex store 获取渠道列表（与 InboxesSection 共享）
const pilotInboxes = useMapGetter('pilotInboxes/getRecords');
const inboxes = computed(() => pilotInboxes.value || []);

const selectedInboxId = ref(null);
const showInboxDropdown = ref(false);
const isLoadingInboxes = ref(false);

const messageInput = ref('');
const messages = ref([]);
const error = ref(null);
const conversationId = ref(null);
const conversationDisplayId = ref(null); // WebSocket 广播用的是 display_id
const runtimeState = ref({});
const messagesContainer = ref(null);
const conversationStatus = ref(null);
const isStartingConversation = ref(false);

// Streaming 超时计时器（30 秒兜底）
const STREAMING_TIMEOUT = 30000;
const streamingTimers = ref({});
// 跟踪正在 streaming 的消息 ID（不依赖 Vuex 的 status 字段）
const streamingMessageIds = ref(new Set());

// WebSocket 断开/重连处理
const lastMessageId = ref(null);

// 独立的 Playground WebSocket 连接
const playgroundActionCable = ref(null);
const playgroundPubsubToken = ref(null);

// 是否显示蒙层（没有会话时显示）
const showOverlay = computed(
  () => !conversationId.value && !isStartingConversation.value
);

// 是否有可用的收件箱
const hasInboxes = computed(() => inboxes.value?.length > 0);

// 是否可以选择收件箱（弹层时可选，聊天中不可选）
const canSelectInbox = computed(() => showOverlay.value);

// ============ 工具函数 ============

// 将 API 消息格式转换为 Dashboard 消息格式（复用）
const convertToDashboardMessage = (msg, convId, accountId) => ({
  id: msg.id,
  content: msg.content,
  account_id: accountId,
  inbox_id: selectedInboxId.value,
  conversation_id: convId,
  message_type: msg.role === 'user' ? 0 : 1,
  created_at: msg.created_at
    ? new Date(msg.created_at).getTime() / 1000
    : Date.now() / 1000,
  updated_at: msg.created_at
    ? new Date(msg.created_at).getTime() / 1000
    : Date.now() / 1000,
  private: false,
  status: 'sent',
  content_type: 'text',
  content_attributes: {},
  additional_attributes: {
    agent_name: msg.agent_name,
    pilot_debug: msg.pilot_debug,
  },
  sender_type: msg.role === 'user' ? 'Contact' : 'Pilot::Assistant',
  sender_id: msg.role === 'user' ? null : props.assistant?.id,
});

// 将 Dashboard 消息格式转换为显示格式
const convertToDisplayMessage = msg => ({
  id: msg.id,
  role: msg.message_type === 0 ? 'user' : 'assistant',
  messageType: msg.message_type, // 保留原始消息类型（2 = activity）
  content: msg.content,
  agentName: msg.additional_attributes?.agent_name || props.assistant?.name,
  pilotDebug: msg.additional_attributes?.pilot_debug || null,
  isStreaming: streamingMessageIds.value.has(msg.id),
});

// 加载助手关联的渠道
const fetchAssistantInboxes = async () => {
  if (!props.assistant?.id) return;

  isLoadingInboxes.value = true;
  try {
    await store.dispatch('pilotInboxes/get', {
      assistantId: props.assistant.id,
    });
  } catch (err) {
    console.error('Failed to fetch assistant inboxes:', err);
  } finally {
    isLoadingInboxes.value = false;
  }
};

// ============ localStorage 收件箱记忆 ============

// 获取 localStorage 的 key
const getStorageKey = key => {
  const assistantId = props.assistant?.id;
  if (!assistantId) return null;
  return `playground_${key}_${assistantId}`;
};

// 保存选中的收件箱到 localStorage
const saveSelectedInboxToStorage = inboxId => {
  const key = getStorageKey('selected_inbox_id');
  if (!key || !inboxId) return;

  try {
    localStorage.setItem(key, String(inboxId));
  } catch (e) {
    console.error('[Playground] Failed to save inbox to localStorage:', e);
  }
};

// 从 localStorage 读取选中的收件箱
const loadSelectedInboxFromStorage = () => {
  const key = getStorageKey('selected_inbox_id');
  if (!key) return null;

  try {
    const inboxId = localStorage.getItem(key);
    if (inboxId) {
      return parseInt(inboxId, 10);
    }
  } catch (e) {
    console.error('[Playground] Failed to read inbox from localStorage:', e);
  }
  return null;
};

// 监听 inboxes 变化，自动选择收件箱
// 优先级：1. localStorage 记忆  2. 最后创建的收件箱（列表第一个）
watch(
  inboxes,
  newInboxes => {
    if (newInboxes?.length > 0 && !selectedInboxId.value) {
      // 先尝试从 localStorage 恢复
      const storedInboxId = loadSelectedInboxFromStorage();
      const storedInbox = storedInboxId
        ? newInboxes.find(i => i.id === storedInboxId)
        : null;

      if (storedInbox) {
        // localStorage 中的收件箱存在，选中它
        selectedInboxId.value = storedInboxId;
      } else {
        // 没有记忆或已失效，选择第一个（最后创建的）
        selectedInboxId.value = newInboxes[0].id;
      }
    }
  },
  { immediate: true }
);

// 选中的 Inbox 名称
const selectedInboxName = computed(() => {
  if (!hasInboxes.value) {
    return t('PILOT.ASSISTANTS.CONFIG.PREVIEW.NO_INBOXES');
  }
  const inbox = inboxes.value?.find(i => i.id === selectedInboxId.value);
  return inbox?.name || t('PILOT.ASSISTANTS.CONFIG.PREVIEW.SELECT_INBOX');
});

// 选择 Inbox
const selectInbox = inboxId => {
  selectedInboxId.value = inboxId;
  showInboxDropdown.value = false;
  // 保存到 localStorage
  saveSelectedInboxToStorage(inboxId);
};

// 跳转到渠道配置页面
const goToChannelConfig = () => {
  if (route.params.section === 'inboxes') return;

  router.push({
    name: 'pilot_assistant_config_section',
    params: {
      accountId: route.params.accountId,
      assistantId: route.params.assistantId,
      section: 'inboxes',
    },
  });
};

// 显示 "..." 的条件（会话状态是 pending 且最后一条消息是 incoming）
const showTypingIndicator = computed(() => {
  if (messages.value.length === 0) return false;
  const lastMessage = messages.value[messages.value.length - 1];
  return conversationStatus.value === 'pending' && lastMessage.role === 'user';
});

// 是否正在等待 AI 响应（包括等待开始 + streaming 进行中）
const isWaitingForResponse = computed(() => {
  if (showTypingIndicator.value) return true;
  return messages.value.some(m => m.isStreaming);
});

// 获取转人工配置
const handoffConfig = computed(() => props.assistant?.config?.handoff || {});

const handoffButtonEnabled = computed(
  () => handoffConfig.value?.manualTrigger?.buttonEnabled ?? false
);

const handoffButtonText = computed(
  () =>
    handoffConfig.value?.manualTrigger?.buttonText ||
    t('PILOT.ASSISTANTS.CONFIG.PREVIEW.MANUAL_HANDOFF')
);

const runtimeAgentStack = computed(() => runtimeState.value?.agent_stack || []);

const hasRuntimeDebugState = computed(
  () =>
    !!conversationId.value &&
    !!(
      runtimeState.value?.current_agent ||
      runtimeState.value?.agent_task ||
      runtimeState.value?.agent_status ||
      runtimeState.value?.interruption ||
      runtimeAgentStack.value.length ||
      runtimeState.value?.active_task_summary ||
      runtimeState.value?.last_handoff_reason ||
      runtimeState.value?.last_route_hint
    )
);

const routeHintLabel = computed(() => {
  const labels = {
    continue_specialist: '续给子 Agent',
    interruption: '回主 Agent',
    orchestrator_review: '主 Agent 判断',
    orchestrator: '主 Agent',
  };

  return labels[runtimeState.value?.last_route_hint] || runtimeState.value?.last_route_hint;
});

const resumeActionLabel = computed(() => {
  if (!runtimeState.value?.active_task_summary) return '';
  return `继续刚才任务：${runtimeState.value.active_task_summary}`;
});

// 自动滚动到底部
const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
};

const applyRuntimeState = state => {
  runtimeState.value = state || {};
};

// ============ WebSocket 断开/重连处理 ============

// 断开时：记录最后消息ID
const setLastMessageId = () => {
  if (messages.value.length === 0) return;
  const lastMessage = messages.value[messages.value.length - 1];
  if (lastMessage && typeof lastMessage.id === 'number') {
    lastMessageId.value = lastMessage.id;
  }
};

// 重连时：同步丢失的消息
const syncLatestMessages = async () => {
  if (!props.assistant?.id || !conversationId.value) return;

  try {
    const response = await pilotAssistantAPI.getPlaygroundMessages(
      props.assistant.id,
      conversationId.value
    );
    const { messages: apiMessages, status } = response.data;
    applyRuntimeState(response.data.runtime_state);

    if (!apiMessages || !Array.isArray(apiMessages)) return;

    // 找出丢失的消息（ID 大于 lastMessageId 的消息）
    const missingMessages = apiMessages.filter(msg => {
      const msgId = msg.id;
      return msgId && lastMessageId.value && msgId > lastMessageId.value;
    });

    if (missingMessages.length === 0) return;

    // 更新会话状态
    if (status) {
      conversationStatus.value = status;
    }

    // 通过 Vuex action 添加丢失的消息
    const accountId = store.getters.getCurrentAccountId;
    missingMessages.forEach(msg => {
      const dashboardMessage = convertToDashboardMessage(
        msg,
        conversationId.value,
        accountId
      );
      store.dispatch('addMessage', {
        ...dashboardMessage,
        conversation_id: conversationId.value,
      });
    });

    scrollToBottom();
  } catch (err) {
    console.error('Failed to sync messages:', err);
  }
};

// 初始化会话到 Vuex store
const initConversationInStore = (id, inboxId, initialMessages = []) => {
  if (!id) return;

  const existingConversation = store.getters.getConversationById(id);
  if (existingConversation) return;

  const accountId = store.getters.getCurrentAccountId;
  const conversation = {
    id,
    account_id: accountId,
    inbox_id: inboxId,
    messages: initialMessages,
    meta: { sender: null, assignee: null, team: null },
    status: 'pending',
    timestamp: Date.now() / 1000,
    created_at: Date.now() / 1000,
    updated_at: Date.now() / 1000,
    unread_count: 0,
    additional_attributes: {
      playground: true,
      assistant_id: props.assistant?.id,
    },
    can_reply: true,
  };

  store.commit('ADD_CONVERSATION', conversation);
};

// 开始对话
const startConversation = async () => {
  if (!props.assistant?.id || isStartingConversation.value) return;

  isStartingConversation.value = true;
  error.value = null;

  try {
    const response = await pilotAssistantAPI.playgroundStartConversation({
      assistantId: props.assistant.id,
      inboxId: selectedInboxId.value,
    });

    conversationId.value = response.data.conversation_id;
    conversationDisplayId.value = response.data.display_id;
    playgroundPubsubToken.value = response.data.pubsub_token;
    applyRuntimeState(response.data.runtime_state);

    // 开场白在 WebSocket 连接建立前就由后端发送了，需要作为初始消息传入 Vuex
    const initialMessages = [];
    if (response.data.greeting) {
      initialMessages.push({
        id: `greeting_${Date.now()}`,
        content: response.data.greeting,
        account_id: store.getters.getCurrentAccountId,
        inbox_id: selectedInboxId.value,
        conversation_id: conversationId.value,
        message_type: 1,
        created_at: Date.now() / 1000,
        updated_at: Date.now() / 1000,
        private: false,
        status: 'sent',
        content_type: 'text',
        content_attributes: {},
        additional_attributes: { agent_name: props.assistant?.name },
        sender_type: 'Pilot::Assistant',
        sender_id: props.assistant?.id,
      });
    }
    initConversationInStore(conversationId.value, selectedInboxId.value, initialMessages);

    // 保存会话信息到 localStorage
    saveConversationToStorage(
      conversationId.value,
      playgroundPubsubToken.value
    );

    // 创建独立的 WebSocket 连接（像 Widget 一样）
    if (playgroundPubsubToken.value) {
      initPlaygroundActionCable();
    }

    scrollToBottom();
  } catch (err) {
    console.error('Failed to start conversation:', err);
    error.value = t('PILOT.ASSISTANTS.CONFIG.PREVIEW.START_FAILED');
  } finally {
    isStartingConversation.value = false;
  }
};

// 重置会话（清空本地状态和 localStorage）
const resetConversation = () => {
  // 保存当前会话 ID，用于清理 Vuex
  const currentConvId = conversationId.value;

  // 断开独立的 WebSocket 连接
  disconnectPlaygroundActionCable();

  // 清理 streaming 计时器
  clearAllStreamingTimers();

  // 清空 localStorage 中的会话信息
  clearConversationFromStorage();

  // 清理 Vuex store 中的会话
  if (currentConvId) {
    store.commit('DELETE_CONVERSATION', currentConvId);
  }

  messages.value = [];
  conversationId.value = null;
  conversationStatus.value = null;
  lastMessageId.value = null;
  error.value = null;
  playgroundPubsubToken.value = null;
  runtimeState.value = {};
};

// ============ localStorage 会话持久化 ============

// 保存会话信息到 localStorage
const saveConversationToStorage = (convId, pubsubToken) => {
  if (!convId || !pubsubToken) return;
  const key1 = getStorageKey('conversation_id');
  const key2 = getStorageKey('pubsub_token');
  if (!key1 || !key2) return;

  try {
    localStorage.setItem(key1, String(convId));
    localStorage.setItem(key2, pubsubToken);
    console.log('[Playground] Saved conversation to localStorage');
  } catch (e) {
    console.error('[Playground] Failed to save to localStorage:', e);
  }
};

// 从 localStorage 读取会话信息
const loadConversationFromStorage = () => {
  const key1 = getStorageKey('conversation_id');
  const key2 = getStorageKey('pubsub_token');
  if (!key1 || !key2) return null;

  try {
    const convId = localStorage.getItem(key1);
    const pubsubToken = localStorage.getItem(key2);
    if (convId && pubsubToken) {
      return { conversationId: parseInt(convId, 10), pubsubToken };
    }
  } catch (e) {
    console.error('[Playground] Failed to read from localStorage:', e);
  }
  return null;
};

// 清空 localStorage 中的会话信息
const clearConversationFromStorage = () => {
  const key1 = getStorageKey('conversation_id');
  const key2 = getStorageKey('pubsub_token');
  if (!key1 || !key2) return;

  try {
    localStorage.removeItem(key1);
    localStorage.removeItem(key2);
    console.log('[Playground] Cleared conversation from localStorage');
  } catch (e) {
    console.error('[Playground] Failed to clear localStorage:', e);
  }
};

// 验证并恢复会话
const validateAndRestoreConversation = async () => {
  const stored = loadConversationFromStorage();
  if (!stored) return false;

  try {
    // 调用 API 验证会话是否存在（不限制状态，模拟用户端体验）
    const response = await pilotAssistantAPI.getPlaygroundMessages(
      props.assistant.id,
      stored.conversationId
    );
    const {
      conversation_id: convId,
      inbox_id: convInboxId,
      pubsub_token: pubsubToken,
      status,
    } = response.data;

    // 检查返回的会话是否和 localStorage 中的一致（不限制状态，模拟用户端体验）
    if (
      convId === stored.conversationId &&
      pubsubToken === stored.pubsubToken &&
      status
    ) {
      // 检查会话的 inbox 是否在当前关联渠道中
      const linkedInboxIds = (inboxes.value || []).map(inbox => inbox.id);
      const isInboxLinked = linkedInboxIds.includes(convInboxId);

      if (!isInboxLinked) {
        // 会话的 inbox 不在当前关联渠道中，清空 localStorage，显示蒙层
        console.log(
          '[Playground] Conversation inbox not linked, clearing localStorage'
        );
        clearConversationFromStorage();
        return false;
      }

      // 会话有效，恢复状态
      conversationId.value = convId;
      conversationDisplayId.value = response.data.display_id;
      playgroundPubsubToken.value = pubsubToken;
      conversationStatus.value = status;
      applyRuntimeState(response.data.runtime_state);

      // 更新选中的收件箱为会话的收件箱
      selectedInboxId.value = convInboxId;

      // 处理消息
      const { messages: apiMessages } = response.data;
      if (apiMessages && Array.isArray(apiMessages)) {
        const accountId = store.getters.getCurrentAccountId;
        const dashboardMessages = apiMessages.map(msg =>
          convertToDashboardMessage(msg, convId, accountId)
        );
        messages.value = apiMessages.map((msg, index) => ({
          id: msg.id || `temp_${Date.now()}_${index}`,
          role: msg.role,
          content: msg.content,
          agentName: msg.agent_name || props.assistant?.name,
          pilotDebug: msg.pilot_debug || null,
        }));
        initConversationInStore(
          convId,
          selectedInboxId.value,
          dashboardMessages
        );
      }

      // 建立 WebSocket 连接
      if (playgroundPubsubToken.value) {
        initPlaygroundActionCable();
      }

      scrollToBottom();
      console.log('[Playground] Restored conversation from localStorage');
      return true;
    }
  } catch (err) {
    console.error('[Playground] Failed to validate stored conversation:', err);
  }

  // 验证失败，清空 localStorage
  clearConversationFromStorage();
  return false;
};

// 初始化独立的 Playground WebSocket 连接
const initPlaygroundActionCable = () => {
  // 先断开之前的连接
  disconnectPlaygroundActionCable();

  if (!playgroundPubsubToken.value) return;

  const accountId = store.getters.getCurrentAccountId;

  playgroundActionCable.value = new PlaygroundActionCableConnector(
    playgroundPubsubToken.value,
    accountId,
    onPlaygroundMessageCreated,
    onPlaygroundDisconnect,
    onPlaygroundReconnect,
    onPlaygroundStreamingStart,
    onPlaygroundStreaming
  );

  console.log('[Playground] WebSocket connected with pubsub_token');
};

// 断开独立的 WebSocket 连接
const disconnectPlaygroundActionCable = () => {
  if (playgroundActionCable.value) {
    try {
      playgroundActionCable.value.disconnect();
    } catch (e) {
      console.error('[Playground] Failed to disconnect WebSocket:', e);
    }
    playgroundActionCable.value = null;
  }
};

// 处理 Playground WebSocket 消息（message.created / message.updated）
const onPlaygroundMessageCreated = data => {
  console.log('[Playground] onMessageCreated:', data?.conversation_id, 'displayId:', conversationDisplayId.value, data?.id);
  // 只处理当前会话的消息（用 display_id 比对，因为 WebSocket 广播用的是 display_id）
  if (!conversationDisplayId.value || data.conversation_id !== conversationDisplayId.value) {
    console.log('[Playground] DROPPED - convId mismatch or null');
    return;
  }

  // 过滤私有备注（与 Widget 体验一致，不显示 private 消息）
  if (data.private) return;

  // 跳过 streaming 状态的消息，由 onPlaygroundStreamingStart 处理
  if (data.status === 'streaming') return;

  // 更新会话状态
  if (data.conversation?.status) {
    conversationStatus.value = data.conversation.status;
  }

  if (data.conversation?.playground_runtime_state) {
    applyRuntimeState(data.conversation.playground_runtime_state);
  }

  // 添加消息到 Vuex store
  store.dispatch('addMessage', {
    ...data,
    conversation_id: conversationId.value,
  });

  scrollToBottom();
};

// Playground WebSocket 断开
const onPlaygroundDisconnect = () => {
  console.log('[Playground] WebSocket disconnected');
  setLastMessageId();
};

// Playground WebSocket 重连
const onPlaygroundReconnect = () => {
  console.log('[Playground] WebSocket reconnected');
  syncLatestMessages();
};

// ============ Streaming 事件处理 ============

// 收到 streaming.start → 标记 streaming ID，添加消息到 Vuex
const onPlaygroundStreamingStart = data => {
  if (!conversationDisplayId.value || data.conversation_id !== conversationDisplayId.value) return;

  const messageId = data.id;

  // 记录 streaming 消息 ID（convertToDisplayMessage 会据此设置 isStreaming）
  if (messageId) {
    streamingMessageIds.value.add(messageId);
  }

  // 添加消息到 Vuex（空的 streaming 消息）
  store.dispatch('addMessage', {
    ...data,
    conversation_id: conversationId.value,
  });

  // 30 秒超时兜底（通过 Vuex 更新，避免与 Vuex watcher 冲突）
  if (messageId) {
    streamingTimers.value[messageId] = setTimeout(() => {
      if (streamingMessageIds.value.has(messageId)) {
        streamingMessageIds.value.delete(messageId);
        // 从 Vuex store 获取当前内容，通过 Vuex 更新
        const conv = store.getters.getConversationById(conversationId.value);
        const existingMsg = conv?.messages?.find(m => m.id === messageId);
        store.dispatch('updateStreamingMessage', {
          message_id: messageId,
          conversation_id: conversationId.value,
          content: existingMsg?.content || '（回复超时）',
          status: 'sent',
        });
      }
      delete streamingTimers.value[messageId];
    }, STREAMING_TIMEOUT);
  }

  scrollToBottom();
};

// 收到 streaming chunk / done → 通过 Vuex 更新（单一数据源，避免与 Dashboard connector 冲突）
const onPlaygroundStreaming = data => {
  if (!conversationDisplayId.value || data.conversation_id !== conversationDisplayId.value) return;

  const { message_id: messageId, content, done } = data;

  if (done) {
    // 清除超时计时器
    if (streamingTimers.value[messageId]) {
      clearTimeout(streamingTimers.value[messageId]);
      delete streamingTimers.value[messageId];
    }
    // 清除 streaming 标记
    streamingMessageIds.value.delete(messageId);

    if (data.error) {
      // 流式出错：从 Vuex 获取当前内容，追加错误提示
      const conv = store.getters.getConversationById(conversationId.value);
      const existingMsg = conv?.messages?.find(m => m.id === messageId);
      store.dispatch('updateStreamingMessage', {
        message_id: messageId,
        conversation_id: conversationId.value,
        content: existingMsg?.content ? `${existingMsg.content}\n\n（回复中断）` : '（回复失败）',
        status: 'sent',
      });
    }
    // 正常完成：streamingMessageIds 已清除，最终状态由 message.created 事件补全
  } else if (content !== undefined) {
    // 更新流式内容（通过 Vuex，Vuex watcher 会自动重建 messages.value）
    store.dispatch('updateStreamingMessage', {
      message_id: messageId,
      conversation_id: conversationId.value,
      content,
    });
  }

  scrollToBottom();
};

// 清理所有 streaming 计时器
const clearAllStreamingTimers = () => {
  Object.values(streamingTimers.value).forEach(timer => clearTimeout(timer));
  streamingTimers.value = {};
  streamingMessageIds.value.clear();
};

// 监听 assistant 变化
watch(
  () => props.assistant?.id,
  async (newId, oldId) => {
    if (newId && newId !== oldId) {
      // 断开旧的 WebSocket 连接，避免泄漏
      disconnectPlaygroundActionCable();

      // 重置状态
      messages.value = [];
      conversationId.value = null;
      conversationStatus.value = null;
      lastMessageId.value = null;
      error.value = null;
      selectedInboxId.value = null;
      playgroundPubsubToken.value = null;
      runtimeState.value = {};

      await fetchAssistantInboxes();

      // 尝试从 localStorage 恢复会话
      await validateAndRestoreConversation();
    }
  },
  { immediate: true }
);

// 监听关联渠道变化，实时响应
watch(
  inboxes,
  async (newInboxes, oldInboxes) => {
    // 忽略首次加载（由 assistant watch 处理）
    if (!oldInboxes) return;

    // 没有会话，不需要处理
    if (!conversationId.value) return;

    const newInboxIds = (newInboxes || []).map(inbox => inbox.id);
    const currentInboxId = selectedInboxId.value;

    // 检查当前会话的 inbox 是否还在关联渠道中
    const isInboxStillLinked = newInboxIds.includes(currentInboxId);

    if (!isInboxStillLinked) {
      // 会话的 inbox 不在了，清空会话，显示蒙层
      console.log('[Playground] Inbox unlinked, resetting conversation');
      resetConversation();
    }
  },
  { deep: true }
);

// 监听 Vuex store 中的会话变化
watch(
  () => {
    if (!conversationId.value) return null;
    return store.getters.getConversationById(conversationId.value);
  },
  conversation => {
    if (!conversation) return;

    if (conversation.status) {
      conversationStatus.value = conversation.status;
    }

    if (conversation.messages && Array.isArray(conversation.messages)) {
      messages.value = conversation.messages
        .filter(msg => !msg.private)
        .map(convertToDisplayMessage);
      scrollToBottom();
    }
  },
  { deep: true }
);

// 头像相关
const hasAvatar = computed(() => !!props.assistant?.avatar_url);

const getInitial = name => {
  if (!name) return 'A';
  return name.charAt(0).toUpperCase();
};

const getColor = name => {
  const colors = [
    '#8B5CF6',
    '#3B82F6',
    '#10B981',
    '#F59E0B',
    '#EF4444',
    '#EC4899',
    '#6366F1',
  ];
  const index = (name?.charCodeAt(0) || 0) % colors.length;
  return colors[index];
};

// 发送消息
const handleSend = async () => {
  if (!messageInput.value.trim() || isWaitingForResponse.value) return;
  if (!props.assistant?.id) {
    error.value = t('PILOT.ASSISTANTS.CONFIG.PREVIEW.SELECT_ASSISTANT_FIRST');
    return;
  }

  if (!conversationId.value) {
    error.value = t('PILOT.ASSISTANTS.CONFIG.PREVIEW.NO_CONVERSATION');
    return;
  }

  const userMessage = messageInput.value.trim();
  messageInput.value = '';

  // 立即添加用户消息
  messages.value.push({
    id: generateTempMessageId(),
    role: 'user',
    content: userMessage,
  });

  conversationStatus.value = 'pending';
  error.value = null;
  scrollToBottom();

  try {
    await pilotAssistantAPI.playground({
      assistantId: props.assistant.id,
      messageContent: userMessage,
      inboxId: selectedInboxId.value,
      conversationId: conversationId.value,
    });
    // WebSocket 会自动更新 Vuex store，watch 会处理
  } catch (err) {
    console.error('Playground error:', err);
    error.value =
      err.response?.data?.error ||
      err.message ||
      t('PILOT.ASSISTANTS.CONFIG.PREVIEW.LOAD_FAILED');
    messages.value.pop();
  }
};

// 点击转人工按钮
const handleHandoffButtonClick = async () => {
  if (isWaitingForResponse.value) return;
  if (!conversationId.value || !props.assistant?.id) return;

  try {
    await pilotAssistantAPI.playgroundHandoff({
      assistantId: props.assistant.id,
      conversationId: conversationId.value,
    });
    // 转人工成功后，会话状态会通过 WebSocket 更新
  } catch (err) {
    console.error('Handoff error:', err);
    error.value =
      err.response?.data?.error ||
      t('PILOT.ASSISTANTS.CONFIG.PREVIEW.HANDOFF_FAILED');
  }
};

// 重试
const handleRetry = async () => {
  if (messages.value.length === 0) return;

  const lastUserMessage = [...messages.value]
    .reverse()
    .find(m => m.role === 'user');
  if (!lastUserMessage) return;

  // 移除最后一条助手消息（如果有）
  if (messages.value[messages.value.length - 1]?.role === 'assistant') {
    messages.value.pop();
  }

  // 重新发送
  messageInput.value = lastUserMessage.content;
  messages.value = messages.value.filter(m => m.id !== lastUserMessage.id);
  await handleSend();
};

const handleResumeTask = async () => {
  if (!runtimeState.value?.active_task_summary || isWaitingForResponse.value) return;

  messageInput.value = `继续刚才的任务：${runtimeState.value.active_task_summary}`;
  await handleSend();
};

// 初始化
onMounted(() => {
  fetchAssistantInboxes();
});

// 清理
onUnmounted(() => {
  // 断开独立的 Playground WebSocket 连接
  disconnectPlaygroundActionCable();
  // 清理 streaming 计时器
  clearAllStreamingTimers();
});
</script>

<template>
  <aside
    class="w-96 flex-shrink-0 flex flex-col bg-n-slate-white dark:bg-n-slate-900 border-l border-n-weak relative"
  >
    <!-- 标题 -->
    <div
      class="relative z-30 px-4 py-3 border-b border-n-weak flex items-center justify-between"
    >
      <div class="flex items-center gap-3">
        <!-- Inbox 选择器 -->
        <div class="relative">
          <button
            type="button"
            class="flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-n-slate-1 dark:bg-n-slate-7 rounded-lg border border-n-slate-4 dark:border-n-slate-6"
            :class="[
              !hasInboxes
                ? 'text-n-slate-8 cursor-not-allowed'
                : canSelectInbox
                  ? 'text-n-slate-11 hover:text-n-slate-12'
                  : 'text-n-slate-10 cursor-default',
            ]"
            :disabled="!canSelectInbox || !hasInboxes"
            @click="
              canSelectInbox &&
                hasInboxes &&
                (showInboxDropdown = !showInboxDropdown)
            "
          >
            <span class="i-lucide-inbox w-3.5 h-3.5" />
            <span class="max-w-[100px] truncate">{{ selectedInboxName }}</span>
            <span
              v-if="canSelectInbox && hasInboxes"
              class="i-lucide-chevron-down w-3 h-3"
            />
          </button>

          <!-- 下拉菜单 -->
          <div
            v-if="showInboxDropdown && canSelectInbox && hasInboxes"
            class="absolute left-0 top-full mt-1 z-50 min-w-[160px] bg-white dark:bg-n-slate-800 rounded-lg border border-n-slate-4 dark:border-n-slate-6 shadow-lg"
          >
            <div
              v-for="inbox in inboxes"
              :key="inbox.id"
              class="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-n-slate-1 dark:hover:bg-n-slate-7"
              :class="
                inbox.id === selectedInboxId
                  ? 'text-n-brand-600 dark:text-n-brand-400'
                  : 'text-n-slate-12'
              "
              @click="selectInbox(inbox.id)"
            >
              <span
                v-if="inbox.id === selectedInboxId"
                class="i-lucide-check w-3.5 h-3.5"
              />
              <span v-else class="w-3.5" />
              <span class="truncate">{{ inbox.name }}</span>
            </div>
          </div>
        </div>
      </div>
      <button
        v-if="conversationId"
        class="text-xs text-n-slate-12 dark:text-n-slate-100 hover:text-n-brand dark:hover:text-n-brand-400 transition-colors"
        @click="resetConversation"
      >
        {{ t('PILOT.ASSISTANTS.CONFIG.PREVIEW.RESTART') }}
      </button>
    </div>

    <div
      v-if="hasRuntimeDebugState"
      class="px-4 py-3 border-b border-n-weak bg-n-slate-50 dark:bg-n-slate-900/70"
    >
      <div class="flex items-center justify-between gap-3 mb-2">
        <span class="text-xs font-medium text-n-slate-12 dark:text-n-slate-100">
          Agent 调试态
        </span>
        <span
          v-if="routeHintLabel"
          class="px-2 py-0.5 text-[11px] rounded-full bg-n-brand-50 text-n-brand-700 dark:bg-n-brand-900/30 dark:text-n-brand-200"
        >
          {{ routeHintLabel }}
        </span>
      </div>

      <div class="flex flex-col gap-1.5 text-xs text-n-slate-11 dark:text-n-slate-300">
        <div v-if="runtimeState.current_agent">
          当前子 Agent：{{ runtimeState.current_agent }}
        </div>
        <div v-if="runtimeState.agent_status">
          当前状态：{{ runtimeState.agent_status }}
        </div>
        <div v-if="runtimeState.active_task_summary">
          未完成任务：{{ runtimeState.active_task_summary }}
        </div>
        <div v-if="runtimeState.agent_task?.title">
          任务标题：{{ runtimeState.agent_task.title }}
        </div>
        <div v-if="runtimeState.agent_task?.reason">
          任务来源：{{ runtimeState.agent_task.reason }}
        </div>
        <div v-if="runtimeState.interruption?.detected">
          中断处理：{{ runtimeState.interruption.handled_by || '主 Agent' }}
        </div>
        <div v-if="runtimeState.interruption?.interrupted_agent">
          被打断 Agent：{{ runtimeState.interruption.interrupted_agent }}
        </div>
        <div v-if="runtimeState.last_handoff_reason">
          最近委派原因：{{ runtimeState.last_handoff_reason }}
        </div>
        <div v-if="runtimeAgentStack.length">
          任务栈：{{
            runtimeAgentStack
              .map(item => item.agent)
              .filter(Boolean)
              .join(' -> ')
          }}
        </div>
        <button
          v-if="runtimeState.active_task_summary && !isWaitingForResponse"
          type="button"
          class="mt-1 inline-flex w-fit items-center gap-1 px-2 py-1 rounded-md bg-n-slate-2 dark:bg-n-slate-7 hover:bg-n-slate-3 dark:hover:bg-n-slate-6 text-[11px] text-n-slate-12 dark:text-n-slate-100 transition-colors"
          @click="handleResumeTask"
        >
          <span class="i-lucide-rotate-ccw w-3 h-3" />
          {{ resumeActionLabel }}
        </button>
      </div>
    </div>

    <!-- 消息列表 -->
    <div
      ref="messagesContainer"
      class="flex-1 overflow-y-auto p-4 flex flex-col gap-6"
    >
      <!-- 空状态 -->
      <div
        v-if="messages.length === 0 && !showTypingIndicator"
        class="flex flex-col items-center justify-center h-full text-center"
      >
        <span
          class="i-ph-chat-circle-dots text-4xl text-n-slate-8 dark:text-n-slate-700 mb-2"
        />
        <p class="text-sm text-n-slate-10">
          {{ t('PILOT.ASSISTANTS.CONFIG.PREVIEW.EMPTY') }}
        </p>
      </div>

      <!-- 消息 -->
      <template v-else>
        <div
          v-for="msg in messages"
          :key="msg.id"
          class="flex flex-col"
          :class="
            msg.messageType === 2
              ? 'items-center'
              : msg.role === 'user'
                ? 'items-end'
                : 'items-start'
          "
        >
          <!-- Activity 消息：居中弱化样式 -->
          <div
            v-if="msg.messageType === 2"
            class="px-3 py-1 text-sm text-n-slate-11 bg-n-alpha-1 rounded-lg whitespace-pre-wrap"
          >
            {{ msg.content }}
          </div>
          <!-- 普通消息：头像 + 气泡 -->
          <template v-else>
            <div class="flex gap-2 items-end">
              <!-- AI 助手头像 -->
              <template v-if="msg.role === 'assistant'">
                <!-- 系统消息特殊头像 -->
                <img
                  v-if="msg.agentName === '系统'"
                  :src="chatwootBotImage"
                  alt="系统"
                  class="w-8 h-8 rounded-full object-cover flex-shrink-0"
                />
                <!-- AI 助手头像 -->
                <template v-else>
                  <img
                    v-if="hasAvatar"
                    :src="assistant.avatar_url"
                    :alt="msg.agentName"
                    class="w-8 h-8 rounded-full object-cover flex-shrink-0"
                  />
                  <div
                    v-else
                    class="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    :style="{
                      backgroundColor: getColor(
                        msg.agentName || assistant?.name
                      ),
                    }"
                  >
                    <span class="text-sm font-semibold text-white">
                      {{ getInitial(msg.agentName || assistant?.name) }}
                    </span>
                  </div>
                </template>
              </template>
              <!-- 消息气泡 -->
              <div
                v-if="msg.content"
                class="px-3 py-2 text-sm whitespace-pre-wrap shadow-lg"
                :class="[
                  msg.role === 'user'
                    ? 'bg-n-brand text-white rounded-tl-2xl rounded-tr-2xl rounded-bl-2xl max-w-xs'
                    : 'bg-n-brand-50 dark:bg-n-slate-800 text-n-slate-12 rounded-tl-2xl rounded-tr-2xl rounded-br-2xl max-w-[80%]',
                ]"
              >
                {{ msg.content }}
              </div>
              <!-- AI 消息无内容时显示 typing 动画（与 Widget 一致） -->
              <div
                v-if="msg.role === 'assistant' && !msg.content"
                class="px-2 py-2 rounded-tl-2xl rounded-tr-2xl rounded-br-2xl max-w-[2.4rem] shadow-lg bg-n-brand-50 dark:bg-n-slate-800"
              >
                <img
                  :src="typingGif"
                  alt="AI is generating a response"
                  class="w-full"
                />
              </div>
            </div>
            <!-- Agent 名称标签 -->
            <div
              v-if="msg.role === 'assistant' && (msg.agentName || msg.pilotDebug)"
              class="mt-1 ml-10 flex flex-col gap-1"
            >
              <span
                v-if="msg.agentName"
                class="text-xs text-n-slate-10"
              >
                {{ msg.agentName }}
              </span>
              <div
                v-if="msg.pilotDebug"
                class="inline-flex flex-col gap-1 px-2 py-1.5 rounded-lg bg-n-alpha-1 text-[11px] text-n-slate-11 max-w-[80%]"
              >
                <span v-if="msg.pilotDebug.route_hint">
                  路由：{{ msg.pilotDebug.route_hint }}
                </span>
                <span v-if="msg.pilotDebug.agent_status">
                  状态：{{ msg.pilotDebug.agent_status }}
                </span>
                <span v-if="msg.pilotDebug.active_task_summary">
                  任务：{{ msg.pilotDebug.active_task_summary }}
                </span>
                <span v-if="msg.pilotDebug.agent_task?.title">
                  任务标题：{{ msg.pilotDebug.agent_task.title }}
                </span>
                <span v-if="msg.pilotDebug.interruption?.detected">
                  中断：{{ msg.pilotDebug.interruption.interrupted_agent || '已检测' }}
                </span>
                <span
                  v-if="msg.pilotDebug.should_offer_task_resume && msg.pilotDebug.resume_task_label"
                >
                  建议恢复：{{ msg.pilotDebug.resume_task_label }}
                </span>
              </div>
            </div>
          </template>
        </div>

        <!-- 加载动画 -->
        <div v-if="showTypingIndicator" class="flex justify-start gap-2">
          <!-- AI 助手头像 -->
          <img
            v-if="hasAvatar"
            :src="assistant.avatar_url"
            :alt="assistant?.name"
            class="w-8 h-8 rounded-full object-cover flex-shrink-0"
          />
          <div
            v-else
            class="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
            :style="{ backgroundColor: getColor(assistant?.name) }"
          >
            <span class="text-sm font-semibold text-white">
              {{ getInitial(assistant?.name) }}
            </span>
          </div>
          <div class="px-3 py-2 rounded-lg bg-n-slate-100 dark:bg-n-slate-800">
            <span class="inline-flex gap-1">
              <span
                class="w-1.5 h-1.5 rounded-full bg-n-slate-8 animate-bounce"
                style="animation-delay: 0ms"
              />
              <span
                class="w-1.5 h-1.5 rounded-full bg-n-slate-8 animate-bounce"
                style="animation-delay: 150ms"
              />
              <span
                class="w-1.5 h-1.5 rounded-full bg-n-slate-8 animate-bounce"
                style="animation-delay: 300ms"
              />
            </span>
          </div>
        </div>

        <!-- 错误提示 -->
        <div v-if="error" class="flex flex-col items-center gap-2">
          <span class="text-sm text-red-500">{{ error }}</span>
          <button
            class="text-xs text-n-brand hover:underline"
            @click="handleRetry"
          >
            {{ t('PILOT.ASSISTANTS.CONFIG.PREVIEW.RETRY') }}
          </button>
        </div>
      </template>
    </div>

    <!-- 输入区 -->
    <div class="p-4 relative">
      <!-- 转人工按钮 -->
      <button
        v-if="handoffButtonEnabled"
        type="button"
        :disabled="isWaitingForResponse"
        class="absolute -top-3 left-4 px-3 py-1 text-xs font-medium rounded-full bg-n-brand text-white shadow-sm hover:bg-n-brand-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all z-10"
        @click="handleHandoffButtonClick"
      >
        {{ handoffButtonText }}
      </button>
      <div class="flex gap-2">
        <input
          v-model="messageInput"
          type="text"
          :placeholder="t('PILOT.ASSISTANTS.CONFIG.PREVIEW.INPUT_PLACEHOLDER')"
          :disabled="isWaitingForResponse"
          class="flex-1 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-slate-white dark:bg-n-slate-800 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand disabled:opacity-50"
          @keyup.enter="handleSend"
        />
        <Button
          icon="i-ph-paper-plane-tilt"
          :disabled="!messageInput.trim() || isWaitingForResponse"
          @click="handleSend"
        />
      </div>
    </div>

    <!-- 蒙层：开始对话 -->
    <Transition
      enter-active-class="transition-opacity duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-200"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="showOverlay"
        class="absolute inset-0 z-20 flex flex-col items-center justify-center bg-gradient-to-b from-n-slate-50 to-n-slate-100 dark:from-n-slate-900 dark:to-n-slate-800"
      >
        <div class="text-center px-6 w-full max-w-[320px]">
          <!-- 助手信息卡片 -->
          <div
            class="bg-white dark:bg-n-slate-800 rounded-2xl shadow-xl p-6 mb-4"
          >
            <!-- 助手头像 -->
            <div class="mb-4 flex justify-center">
              <div class="relative">
                <img
                  v-if="hasAvatar"
                  :src="assistant.avatar_url"
                  :alt="assistant?.name"
                  class="w-20 h-20 rounded-full object-cover ring-4 ring-n-slate-100 dark:ring-n-slate-700"
                />
                <div
                  v-else
                  class="w-20 h-20 rounded-full flex items-center justify-center ring-4 ring-n-slate-100 dark:ring-n-slate-700"
                  :style="{ backgroundColor: getColor(assistant?.name) }"
                >
                  <span class="text-2xl font-bold text-white">
                    {{ getInitial(assistant?.name) }}
                  </span>
                </div>
                <!-- 在线状态指示器 -->
                <span
                  class="absolute bottom-1 right-1 w-4 h-4 bg-green-500 border-2 border-white dark:border-n-slate-800 rounded-full"
                />
              </div>
            </div>

            <!-- 助手名称 -->
            <h3
              class="text-xl font-semibold text-n-slate-12 dark:text-n-slate-100 mb-2"
            >
              {{ assistant?.name || 'AI 助手' }}
            </h3>

            <!-- 助手描述 -->
            <p
              class="text-sm text-n-slate-10 dark:text-n-slate-400 leading-relaxed"
            >
              {{
                assistant?.description ||
                t('PILOT.ASSISTANTS.CONFIG.PREVIEW.DESCRIPTION')
              }}
            </p>
          </div>

          <!-- 开始对话按钮 -->
          <button
            v-if="hasInboxes || isLoadingInboxes"
            type="button"
            class="w-full inline-flex items-center justify-center gap-2 px-6 py-3 text-base font-medium text-white bg-n-brand rounded-xl shadow-lg hover:bg-n-brand-600 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
            :disabled="isStartingConversation"
            @click="startConversation"
          >
            <span
              v-if="isStartingConversation"
              class="i-lucide-loader-2 w-5 h-5 animate-spin"
            />
            <span v-else class="i-lucide-message-circle w-5 h-5" />
            <span>{{
              isStartingConversation
                ? t('PILOT.ASSISTANTS.CONFIG.PREVIEW.STARTING')
                : t('PILOT.ASSISTANTS.CONFIG.PREVIEW.START_CONVERSATION')
            }}</span>
          </button>

          <!-- 没有关联渠道：引导卡片 -->
          <div
            v-else
            class="w-full bg-n-slate-50 dark:bg-n-slate-800 rounded-xl p-6 text-center"
          >
            <span
              class="i-lucide-inbox w-10 h-10 text-n-slate-300 dark:text-n-slate-500 mx-auto mb-3"
            />
            <p class="text-sm text-n-slate-500 dark:text-n-slate-400 mb-4">
              {{ t('PILOT.ASSISTANTS.CONFIG.PREVIEW.NO_INBOX_HINT') }}
            </p>
            <button
              type="button"
              class="inline-flex items-center justify-center gap-1.5 px-6 py-2.5 text-sm font-medium text-white bg-n-brand rounded-lg hover:bg-n-brand-600 transition-colors"
              @click="goToChannelConfig"
            >
              <span class="i-lucide-plus w-4 h-4" />
              {{ t('PILOT.ASSISTANTS.CONFIG.PREVIEW.GO_LINK_INBOX') }}
            </button>
            <p class="text-xs text-n-slate-500 dark:text-n-slate-400 mt-3">
              {{ t('PILOT.ASSISTANTS.CONFIG.PREVIEW.INBOX_LINK_HINT') }}
            </p>
          </div>
        </div>

        <!-- 底部装饰 -->
        <div class="absolute bottom-6 left-0 right-0 flex justify-center gap-1">
          <span
            class="w-1.5 h-1.5 rounded-full bg-n-slate-300 dark:bg-n-slate-600"
          />
          <span class="w-1.5 h-1.5 rounded-full bg-n-brand" />
          <span
            class="w-1.5 h-1.5 rounded-full bg-n-slate-300 dark:bg-n-slate-600"
          />
        </div>
      </div>
    </Transition>
  </aside>
</template>
